import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Box,
  makeStyles,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import {
  Close,
  Settings,
  Storage,
  BarChart,
  Notifications,
} from "@material-ui/icons";
import {
  useCookieConsent,
  CookiePreferences,
} from "../../contexts/CookieConsentContext";

const useStyles = makeStyles(theme => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#1a1a1a",
      color: "#fff",
      minWidth: "500px",
      maxWidth: "600px",
      zIndex: 10000,
      [theme.breakpoints.down("sm")]: {
        minWidth: "unset",
        maxWidth: "100%",
        width: "100%",
        margin: 0,
        maxHeight: "100vh",
        height: "100vh",
        borderRadius: 0,
      },
    },
    "& .MuiBackdrop-root": {
      zIndex: 9999,
    },
  },
  title: {
    color: "#FE5A97",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
      paddingRight: theme.spacing(5),
    },
  },
  closeButton: {
    color: "#ccc",
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      minWidth: "44px",
      minHeight: "44px",
    },
  },
  content: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      maxHeight: "calc(100vh - 140px)",
      overflowY: "auto",
    },
  },
  section: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    color: "#FE5A97",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "15px",
      marginBottom: theme.spacing(0.75),
    },
  },
  description: {
    color: "#ccc",
    fontSize: "14px",
    marginBottom: theme.spacing(2),
    lineHeight: 1.4,
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      marginBottom: theme.spacing(1.5),
      lineHeight: 1.5,
    },
  },
  switchLabel: {
    color: "#fff",
    "& .MuiFormControlLabel-label": {
      color: "#fff",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
        paddingLeft: theme.spacing(1),
      },
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      marginRight: 0,
      width: "100%",
      justifyContent: "space-between",
      padding: theme.spacing(1, 0),
      minHeight: "48px",
    },
  },
  disabledLabel: {
    color: "#666",
    "& .MuiFormControlLabel-label": {
      color: "#666",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
        paddingLeft: theme.spacing(1),
      },
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      marginRight: 0,
      width: "100%",
      justifyContent: "space-between",
      padding: theme.spacing(1, 0),
      minHeight: "48px",
    },
  },
  switch: {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#FE5A97",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#FE5A97",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  switchMobile: {
    [theme.breakpoints.down("sm")]: {
      "& .MuiSwitch-root": {
        width: 58,
        height: 38,
      },
      "& .MuiSwitch-thumb": {
        width: 18,
        height: 18,
      },
    },
  },
  actions: {
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      flexDirection: "column-reverse",
      gap: theme.spacing(1),
      "& > *": {
        width: "100%",
        margin: "0 !important",
      },
    },
  },
  saveButton: {
    backgroundColor: "#FE5A97",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#e04a7a",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      fontSize: "16px",
      fontWeight: 600,
    },
  },
  cancelButton: {
    backgroundColor: "transparent",
    color: "#ccc",
    border: "1px solid #666",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25),
      fontSize: "14px",
    },
  },
  divider: {
    backgroundColor: "#333",
    margin: theme.spacing(2, 0),
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1.5, 0),
    },
  },
  infoBox: {
    backgroundColor: "#2a2a2a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
  },
  infoText: {
    color: "#ccc",
    fontSize: "13px",
    lineHeight: 1.4,
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      lineHeight: 1.5,
    },
  },
}));

interface CookiePreferencesDialogProps {
  open: boolean;
  onClose: () => void;
}

const CookiePreferencesDialog: React.FC<CookiePreferencesDialogProps> = ({
  open,
  onClose,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { preferences, savePreferences, rejectAll } = useCookieConsent();
  const [localPreferences, setLocalPreferences] =
    useState<CookiePreferences>(preferences);

  React.useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  const handlePreferenceChange =
    (key: keyof CookiePreferences) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (key === "essential") {
        // Essential cookies cannot be disabled
        return;
      }

      setLocalPreferences(prev => ({
        ...prev,
        [key]: event.target.checked,
      }));
    };

  const handleSave = () => {
    savePreferences(localPreferences);
    onClose();
  };

  const handleCancel = () => {
    setLocalPreferences(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      functional: true,
      advertising: true,
    };
    setLocalPreferences(allAccepted);
    savePreferences(allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    // Use rejectAll from context to ensure banner is closed immediately
    rejectAll();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle className={classes.title}>
        <Storage />
        Cookie Preferences
        <IconButton
          className={classes.closeButton}
          onClick={handleCancel}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.infoBox}>
          <Typography className={classes.infoText}>
            We use cookies to enhance your experience and analyze site usage.
            You can customize your preferences below. Essential cookies are
            required for the site to function and cannot be disabled.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <Settings />
            Essential Cookies
          </Typography>
          <Typography className={classes.description}>
            Required for the website to function properly. These include
            authentication, security, and basic navigation features.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.essential}
                onChange={handlePreferenceChange("essential")}
                className={`${classes.switch} ${classes.switchMobile}`}
                disabled
              />
            }
            label="Essential Cookies (Required)"
            className={classes.disabledLabel}
          />
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <BarChart />
            Analytics Cookies
          </Typography>
          <Typography className={classes.description}>
            Help us understand how visitors interact with our website by
            collecting and reporting information anonymously. Used by Google
            Analytics and Firebase Analytics.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.analytics}
                onChange={handlePreferenceChange("analytics")}
                className={`${classes.switch} ${classes.switchMobile}`}
              />
            }
            label="Analytics Cookies"
            className={classes.switchLabel}
          />
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <Settings />
            Functional Cookies
          </Typography>
          <Typography className={classes.description}>
            Enable enhanced functionality and personalization, such as
            remembering your preferences and login state.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.functional}
                onChange={handlePreferenceChange("functional")}
                className={`${classes.switch} ${classes.switchMobile}`}
              />
            }
            label="Functional Cookies"
            className={classes.switchLabel}
          />
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <Notifications />
            Advertising Cookies
          </Typography>
          <Typography className={classes.description}>
            Used to track visitors across websites to display relevant and
            engaging advertisements. Currently not used but may be added in
            future updates.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.advertising}
                onChange={handlePreferenceChange("advertising")}
                className={`${classes.switch} ${classes.switchMobile}`}
              />
            }
            label="Advertising Cookies"
            className={classes.switchLabel}
          />
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          variant="outlined"
          className={classes.cancelButton}
          onClick={handleRejectAll}
          fullWidth={isMobile}
        >
          Reject All
        </Button>
        <Button
          variant="outlined"
          className={classes.cancelButton}
          onClick={handleAcceptAll}
          fullWidth={isMobile}
        >
          Accept All
        </Button>
        <Button
          variant="contained"
          className={classes.saveButton}
          onClick={handleSave}
          fullWidth={isMobile}
        >
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookiePreferencesDialog;
